import * as authorizationService from '@/services/authorization';
import * as userService from '@/services/user';
import { COMPLETE_LOADING, START_LOADING } from '@/store';
import * as interactionService from '@/services/interaction';
import * as types from './constants';

const actions = {
  async loginRequest({ commit }, payload) {
    const { user, redirectUrl } = payload;
    try {
      const loginResponse = await authorizationService.loginRequest(user, redirectUrl);
      commit(types.LOGIN_SUCCESS);
      return loginResponse;
    } catch (e) {
      commit(types.LOGIN_FAILURE);
      throw e;
    }
  },

  async logout() {
    await authorizationService.logoutRequest();
  },

  async getUser({ commit }) {
    const user = await userService.userRequest();
    commit(types.SET_USER, user);
    return user;
  },

  async getAccount({ commit }) {
    const account = await userService.accountRequest();
    commit(types.SET_ACCOUNT, account);
    return account;
  },

  async createClient({ commit }, payload) {
    const { client, productConfigurationId } = payload;
    try {
      const newClientData = await userService.createClientRequest(client, productConfigurationId);
      commit(types.CREATE_CLIENT_SUCCESS);
      return newClientData;
    } catch (e) {
      commit(types.CREATE_CLIENT_FAILURE);
      throw e;
    }
  },

  async deleteClient({ commit }, payload) {
    const { clientToken, productType } = payload;
    try {
      await userService.deleteClientRequest(clientToken, productType);
      commit(types.DELETE_CLIENT_SUCCESS);
    } catch (e) {
      commit(types.DELETE_CLIENT_FAILURE);
      throw e;
    }
  },

  async changePassword({ commit }, payload) {
    commit(START_LOADING);
    const { currentPassword, password, id } = payload;
    try {
      await userService.changePassword(id, currentPassword, password);
      commit(types.CHANGE_PASSWORD_SUCCESS);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(types.CHANGE_PASSWORD_FAILURE);
      commit(COMPLETE_LOADING);
      throw e;
    }
  },

  async updateClient({ commit }, payload) {
    const showLoadingIndicator = !payload.skipLoadingIndicator;
    const { client } = payload;
    if (showLoadingIndicator) commit(START_LOADING);
    try {
      await userService.updateClient(client);
      commit(types.UPDATE_CLIENT_SUCCESS);
      if (showLoadingIndicator) commit(COMPLETE_LOADING);
    } catch (e) {
      commit(types.UPDATE_CLIENT_FAILURE);
      if (showLoadingIndicator) commit(COMPLETE_LOADING);
      throw e;
    }
  },

  async updateClientSettings({ commit, dispatch }, payload) {
    commit(START_LOADING);
    try {
      await userService.updateClientSettings(payload).then(() => dispatch('getUser'));
      commit(types.UPDATE_CLIENT_SUCCESS);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(types.UPDATE_CLIENT_FAILURE);
      commit(COMPLETE_LOADING);
      throw e;
    }
  },

  async recoverPassword({ commit }, payload) {
    commit(START_LOADING);
    try {
      await userService.recoverPassword(payload);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(COMPLETE_LOADING);
      throw e;
    }
  },

  async resetPassword({ commit }, payload) {
    commit(START_LOADING);
    try {
      await userService.resetPassword(payload);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(COMPLETE_LOADING);
      throw e;
    }
  },

  async verifyPasswordResetToken({ commit }, payload) {
    commit(START_LOADING);
    try {
      await userService.verifyPasswordResetToken(payload);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(COMPLETE_LOADING);
      throw e;
    }
  },

  async fetchCreateUserLimits({ commit }, payload) {
    commit(START_LOADING);
    const { initiatorId } = payload;
    try {
      const interactionLimits = await interactionService.fetchCreateUserLimits(initiatorId);
      commit(types.FETCH_CREATE_USER_LIMITS_SUCCESS, interactionLimits.limit);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(types.FETCH_CREATE_USER_LIMITS_FAILURE, e);
      commit(COMPLETE_LOADING);
    }
  },

  async addUserToAnonymizationAllowList({ commit }, payload) {
    commit(START_LOADING);
    const { productConfigurationId, bqUserToken } = payload;

    try {
      await userService.addUserToAnonymizationAllowList(productConfigurationId, bqUserToken);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(COMPLETE_LOADING);
    }
  },

  async removeUserFromAnonymizationAllowList({ commit }, payload) {
    commit(START_LOADING);
    const { productConfigurationId, bqUserToken } = payload;

    try {
      await userService.removeUserFromAnonymizationAllowList(productConfigurationId, bqUserToken);
      commit(COMPLETE_LOADING);
    } catch (e) {
      commit(COMPLETE_LOADING);
    }
  },

  setCurrentProduct({ commit }, payload) {
    commit(types.SET_CURRENT_PRODUCT, payload);
  },

  async submitEmail(_, payload) {
    await userService.submitEmail(payload);
  },

  async authenticateWithGoogle({ commit }, payload) {
    const { productType, data } = payload;
    commit(types.SOCIALS_AUTH_LOADING_START);
    const response = await userService.authenticateWithGoogle(productType, data);
    if (response.data && response.data.redirectUrl) {
      commit(types.SOCIALS_AUTH_RESPONSE, response.data.redirectUrl);
      commit(types.SOCIALS_AUTH_LOADING_COMPLETE);
    } else {
      commit(types.SOCIALS_AUTH_LOADING_COMPLETE);
      return response;
    }
  },

  async register(_, payload) {
    await userService.register(payload);
  },

  async submitEmailBQPSelfAssessment(_, payload) {
    await userService.submitEmailBQPSelfAssessment(payload);
  },

  async registerBQPSelfAssessment(_, payload) {
    await userService.registerBQPSelfAssessment(payload);
  },

  async verifyEmailToken(_, payload) {
    return userService.verifyEmailToken(payload.emailVerificationToken);
  },

  async fetchPrivacyPolicyData({ commit }, payload) {
    const { accountId } = payload;
    commit(START_LOADING);
    try {
      const privacyPolicyData = await userService.fetchPrivacyPolicyData(accountId);
      commit(types.FETCH_PRIVACY_POLICY_DATA_SUCCESS, privacyPolicyData);
    } catch (e) {
      commit(types.FETCH_PRIVACY_POLICY_DATA_FAILURE);
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async updatePrivacyPolicyData({ commit }, payload) {
    commit(START_LOADING);
    try {
      await userService.updatePrivacyPolicyData(payload);
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async fetchAPIKey({ commit }) {
    commit(START_LOADING);
    try {
      const apiKeyData = await userService.fetchAPIKey();
      return apiKeyData;
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async createAPIKey({ commit }, payload) {
    const { description } = payload;

    commit(START_LOADING);
    try {
      const apiKeyResponse = await userService.createAPIKey(description);
      return apiKeyResponse;
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async revokeAPIKey({ commit }, payload) {
    const { id } = payload;

    commit(START_LOADING);
    try {
      await userService.revokeAPIKey(id);
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async fetchEmailTemplates({ commit }, payload) {
    const { userId, productType } = payload;
    commit(START_LOADING);
    try {
      const emailTemplates = await userService.fetchEmailTemplates(userId, productType);
      return emailTemplates;
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async createEmailTemplate(base, payload) {
    const { userId, productType, template } = payload;

    const apiKeyResponse = await userService.createEmailTemplate(userId, productType, template);
    return apiKeyResponse;
  },

  async updateEmailTemplate(base, payload) {
    const { userId, templateId, productType, template } = payload;

    const apiKeyResponse = await userService.updateEmailTemplate(userId, productType, templateId, template);
    return apiKeyResponse;
  },

  async deleteEmailTemplate(base, payload) {
    const { userId, productType, templateId } = payload;

    const apiKeyResponse = await userService.deleteEmailTemplate(userId, productType, templateId);
    return apiKeyResponse;
  },

  async createWhitelabelConfig(base, payload) {
    const { accountId, logo, colorReport } = payload;
    const formData = new FormData();
    formData.append('logo', logo);
    formData.append('colorReport', colorReport);

    const response = await userService.createWhitelabelConfig(accountId, formData);
    return response;
  },

  async updateWhitelabelConfig(base, payload) {
    const { accountId, logo, colorReport } = payload;

    const formData = new FormData();
    if (logo) formData.append('logo', logo);
    if (colorReport) formData.append('colorReport', colorReport);

    const response = await userService.updateWhitelabelConfig(accountId, formData);
    return response;
  },

  async deleteWhitelabelConfig(base, payload) {
    const { accountId } = payload;

    await userService.deleteWhitelabelConfig(accountId);
  },

  async fetchCustomPortfolios(base, payload) {
    const portfolios = await userService.fetchCustomPortfolios(payload.accountId, payload.paginationParams);
    return portfolios;
  },

  async fetchAccountCurrencies(base, payload) {
    return userService.fetchAccountCurrencies(payload.accountId);
  },

  async createCustomPortfolio(base, payload) {
    const { accountId, portfolio } = payload;
    const result = await userService.createCustomPortfolio(accountId, portfolio);
    return result;
  },

  async fetchCustomPortfolio({ commit }, payload) {
    const { accountId, portfolioId } = payload;
    commit(START_LOADING);
    try {
      const portfolio = await userService.fetchCustomPortfolio(accountId, portfolioId);
      return portfolio;
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async updateCustomPortfolio(base, payload) {
    const { accountId, portfolioId, portfolio } = payload;
    const result = await userService.updateCustomPortfolio(accountId, portfolioId, portfolio);
    return result;
  },

  async updateCustomPortfolioStatus(base, payload) {
    const { accountId, portfolioId, status } = payload;
    return userService.updateCustomPortfolioStatus(accountId, portfolioId, status);
  },

  async deleteCustomPortfolio({ commit }, payload) {
    const { accountId, portfolioId } = payload;
    commit(START_LOADING);
    try {
      return await userService.deleteCustomPortfolio(accountId, portfolioId);
    } finally {
      commit(COMPLETE_LOADING);
    }
  },

  async fetchCustomPortfolioAllocation(_, payload) {
    const { accountId, userId } = payload;
    return userService.fetchCustomPortfolioAllocation(accountId, userId);
  },

  async saveCustomPortfolioAllocation(_, payload) {
    const { accountId, userId, allocation } = payload;
    return userService.saveCustomPortfolioAllocation(accountId, userId, allocation);
  },
};

export default actions;
